// QuestionSheet — the assistant's question in the composer's place (pen "Chat
// – every entry" §4, "Fråga · i kompositörens plats"). A question waits where
// you type: one question at a time, every one optional (Skip), a choice always
// ends with "Something else – write it here" (typing there selects it), and a
// scale names every level in words. "Write a message instead" (or the
// chevron) sets it aside as a bar above the composer.
//
// The draft is the form's own (app/formDraftStore, keyed by request id, so it
// survives a remount and a reload); the current question is view-ephemeral.
// Answers submit once, at the end, as one `supply_information`.

import { exhaustSwitchCase } from "@bilda/utils";
import { msg, plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import {
    type FormDraft,
    readFormDraft,
    setDraftSingle,
    toggleDraftMulti,
    writeFormDraft,
} from "../../../app/formDraftStore.js";
import { Button } from "../../Button/Button.js";
import { Icon } from "../../Icon/Icon.js";
import {
    clampQuestionIndex,
    type FormQuestion,
    firstUnansweredIndex,
    sheetAnswers,
    toQuestions,
} from "../InformationRequestCard/formQuestions.js";
import {
    type ChatChoiceOption,
    type ChatField,
    type ChatFieldAnswer,
    type ChatScaleField,
    FALLBACK_FIELD_KEY,
    type FormSourceOption,
    type InformationRequestEntry,
    OWN_ANSWER_FIELD_KEY,
} from "../types.js";
import "./QuestionSheet.css";

type Mark = "radio" | "check";

// The drawn mark over a row's real radio or box (which keeps the row an
// input to assistive tech and the keyboard).
const DrawnMark = ({
    mark,
    selected,
}: {
    readonly mark: Mark;
    readonly selected: boolean;
}): ReactNode => (
    <span
        className={`bilda-qsheet__mark bilda-qsheet__mark--${mark}`}
        aria-hidden="true"
    >
        {mark === "check" && selected ? (
            <Icon name="check" className="bilda-qsheet__tick" />
        ) : null}
    </span>
);

// One answerable row: its mark (a radio or a box), the words, an optional
// meaning under them.
const OptionRow = ({
    mark,
    name,
    selected,
    label,
    meaning,
    onSelect,
}: {
    readonly mark: Mark;
    readonly name: string;
    readonly selected: boolean;
    readonly label: string;
    readonly meaning?: string;
    readonly onSelect: () => void;
}): ReactNode => (
    <label className="bilda-qsheet__option" data-checked={selected}>
        <input
            type={mark === "radio" ? "radio" : "checkbox"}
            name={name}
            className="bilda-qsheet__native"
            checked={selected}
            onChange={onSelect}
        />
        <DrawnMark mark={mark} selected={selected} />
        <span className="bilda-qsheet__words">
            <span className="bilda-qsheet__label">{label}</span>
            {meaning === undefined ? null : (
                <span className="bilda-qsheet__meaning">{meaning}</span>
            )}
        </span>
    </label>
);

// "Something else – write it here": the always-there way out of a choice.
// Typing selects it (the caller decides what selecting means).
const OwnAnswerRow = ({
    mark,
    name,
    selected,
    value,
    onType,
}: {
    readonly mark: Mark;
    readonly name: string;
    readonly selected: boolean;
    readonly value: string;
    readonly onType: (text: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <div
            className="bilda-qsheet__option bilda-qsheet__option--own"
            data-checked={selected}
        >
            <input
                type={mark === "radio" ? "radio" : "checkbox"}
                name={name}
                className="bilda-qsheet__native"
                aria-label={t`Something else – write it here`}
                checked={selected}
                onChange={() => onType(value)}
            />
            <DrawnMark mark={mark} selected={selected} />
            <input
                className="bilda-qsheet__own-input"
                value={value}
                aria-label={t`Something else – write it here`}
                placeholder={t`Something else – write it here`}
                onChange={(e) => onType(e.target.value)}
            />
            {value === "" ? (
                <Icon name="own-answer" className="bilda-qsheet__own-icon" />
            ) : null}
        </div>
    );
};

// The bars that show a level's place in the scale (pen "Nivå").
const LevelBars = ({
    count,
    filled,
}: {
    readonly count: number;
    readonly filled: number;
}): ReactNode => (
    <span className="bilda-qsheet__bars" aria-hidden="true">
        {Array.from({ length: count }, (_, i) => (
            <span
                // biome-ignore lint/suspicious/noArrayIndexKey: the bars are positions, nothing else
                key={i}
                className={`bilda-qsheet__bar${i < filled ? " bilda-qsheet__bar--on" : ""}`}
            />
        ))}
    </span>
);

// The levels a scale offers, in words: the agent's, or (older forms) each
// value with the end labels at the ends.
const scaleLevels = (
    field: ChatScaleField,
): readonly { readonly label: string; readonly meaning?: string }[] =>
    field.levels ??
    Array.from({ length: field.max - field.min + 1 }, (_, i) => {
        const value = field.min + i;
        const end =
            value === field.min
                ? field.minLabel
                : value === field.max
                  ? field.maxLabel
                  : undefined;
        return end === undefined
            ? { label: String(value) }
            : { label: String(value), meaning: end };
    });

const QuestionFields = ({
    question,
    draft,
    sources,
    onSetSingle,
    onToggleMulti,
}: {
    readonly question: FormQuestion;
    readonly draft: FormDraft;
    readonly sources: readonly FormSourceOption[];
    readonly onSetSingle: (key: string, value: string) => void;
    readonly onToggleMulti: (key: string, value: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    const own = question.fields.find((f) => f.key === OWN_ANSWER_FIELD_KEY);
    const ownText = own === undefined ? "" : (draft.single[own.key] ?? "");
    const main = question.fields.find((f) => f.key !== OWN_ANSWER_FIELD_KEY);
    if (main === undefined) return null;
    const options = (
        field: ChatField & { readonly options: readonly ChatChoiceOption[] },
    ) => field.options;
    const control = (field: ChatField): ReactNode => {
        switch (field.type) {
            case "field_text":
                return (
                    <textarea
                        className="bilda-qsheet__text"
                        rows={2}
                        value={draft.single[field.key] ?? ""}
                        placeholder={
                            field.key === FALLBACK_FIELD_KEY
                                ? t`Type your answer`
                                : field.placeholder
                        }
                        onChange={(e) => onSetSingle(field.key, e.target.value)}
                    />
                );
            case "field_single_choice": {
                const picked = draft.single[field.key] ?? "";
                return (
                    <div className="bilda-qsheet__options" role="radiogroup">
                        {options(field).map((o) => (
                            <OptionRow
                                key={o.value}
                                name={field.key}
                                mark="radio"
                                selected={picked === o.value}
                                label={o.label}
                                meaning={o.help}
                                onSelect={() => onSetSingle(field.key, o.value)}
                            />
                        ))}
                        {own === undefined ? null : (
                            <OwnAnswerRow
                                name={field.key}
                                mark="radio"
                                selected={picked === "" && ownText !== ""}
                                value={ownText}
                                onType={(text) => {
                                    onSetSingle(field.key, "");
                                    onSetSingle(own.key, text);
                                }}
                            />
                        )}
                    </div>
                );
            }
            case "field_multi_choice": {
                const picked = draft.multi[field.key] ?? [];
                return (
                    <div className="bilda-qsheet__options">
                        {options(field).map((o) => (
                            <OptionRow
                                key={o.value}
                                name={field.key}
                                mark="check"
                                selected={picked.includes(o.value)}
                                label={o.label}
                                meaning={o.help}
                                onSelect={() =>
                                    onToggleMulti(field.key, o.value)
                                }
                            />
                        ))}
                        {own === undefined ? null : (
                            <OwnAnswerRow
                                name={field.key}
                                mark="check"
                                selected={ownText !== ""}
                                value={ownText}
                                onType={(text) => onSetSingle(own.key, text)}
                            />
                        )}
                    </div>
                );
            }
            case "field_scale": {
                const levels = scaleLevels(field);
                const picked = draft.single[field.key];
                return (
                    <div className="bilda-qsheet__options" role="radiogroup">
                        {levels.map((level, i) => {
                            const value = String(field.min + i);
                            return (
                                <label
                                    key={value}
                                    className="bilda-qsheet__option bilda-qsheet__option--level"
                                    data-checked={picked === value}
                                >
                                    <input
                                        type="radio"
                                        name={field.key}
                                        className="bilda-qsheet__native"
                                        checked={picked === value}
                                        onChange={() =>
                                            onSetSingle(field.key, value)
                                        }
                                    />
                                    <LevelBars
                                        count={levels.length}
                                        filled={i + 1}
                                    />
                                    <span className="bilda-qsheet__words">
                                        <span className="bilda-qsheet__label">
                                            {level.label}
                                        </span>
                                        {level.meaning === undefined ? null : (
                                            <span className="bilda-qsheet__meaning">
                                                {level.meaning}
                                            </span>
                                        )}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                );
            }
            case "field_sources": {
                const picked = draft.multi[field.key] ?? [];
                return (
                    <div className="bilda-qsheet__options">
                        {sources.map((s) => (
                            <OptionRow
                                key={s.id}
                                name={field.key}
                                mark="check"
                                selected={picked.includes(s.id)}
                                label={s.title}
                                onSelect={() => onToggleMulti(field.key, s.id)}
                            />
                        ))}
                    </div>
                );
            }
            default:
                return exhaustSwitchCase(field);
        }
    };
    return (
        <>
            <p className="bilda-qsheet__question">
                {main.key === FALLBACK_FIELD_KEY ? t`Your answer` : main.label}
            </p>
            {main.help === undefined ? null : (
                <p className="bilda-qsheet__help">{main.help}</p>
            )}
            {control(main)}
        </>
    );
};

export const QuestionSheet = ({
    entry,
    sources = [],
    onSubmit,
    onSkip,
    onSetAside,
}: {
    readonly entry: InformationRequestEntry;
    readonly sources?: readonly FormSourceOption[];
    readonly onSubmit?: (answers: readonly ChatFieldAnswer[]) => void;
    readonly onSkip: () => void;
    readonly onSetAside: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const [draft, setDraft] = useState<FormDraft>(() =>
        readFormDraft(entry.id),
    );
    const questions = toQuestions(entry.steps);
    const count = questions.length;
    const [questionIndex, setQuestionIndex] = useState(() =>
        firstUnansweredIndex(questions, draft),
    );
    const index = clampQuestionIndex(questionIndex, count);
    const current = questions[index];
    const last = index >= count - 1;
    const update = (next: FormDraft) => {
        setDraft(next);
        writeFormDraft(entry.id, next);
    };
    const n = index + 1;
    return (
        <section
            className="bilda-qsheet"
            aria-label={t`Question from the assistant`}
        >
            <header className="bilda-qsheet__head">
                <Icon name="sparkle" className="bilda-qsheet__spark" />
                <span className="bilda-qsheet__kicker">
                    <Trans>Question from the assistant</Trans>
                </span>
                {count > 1 ? (
                    <span className="bilda-qsheet__count">
                        <Trans>
                            {n} of {count}
                        </Trans>
                    </span>
                ) : null}
                <button
                    type="button"
                    className="bilda-qsheet__aside"
                    aria-label={t`Write a message instead`}
                    onClick={onSetAside}
                >
                    <Icon name="chevron-down" />
                </button>
            </header>
            {current === undefined ? null : (
                <QuestionFields
                    key={current.key}
                    question={current}
                    draft={draft}
                    sources={sources}
                    onSetSingle={(key, value) =>
                        update(setDraftSingle(draft, key, value))
                    }
                    onToggleMulti={(key, value) =>
                        update(toggleDraftMulti(draft, key, value))
                    }
                />
            )}
            <footer className="bilda-qsheet__foot">
                <button
                    type="button"
                    className="bilda-qsheet__instead"
                    onClick={onSetAside}
                >
                    <Trans>Write a message instead</Trans>
                </button>
                <Button variant="ghost" size="md" onClick={onSkip}>
                    <Trans>Skip</Trans>
                </Button>
                {last ? (
                    onSubmit === undefined ? null : (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={() =>
                                onSubmit(
                                    sheetAnswers(entry.steps, draft, sources),
                                )
                            }
                        >
                            <Trans>Send</Trans>
                        </Button>
                    )
                ) : (
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() => setQuestionIndex(index + 1)}
                    >
                        <Trans>Next</Trans>
                    </Button>
                )}
            </footer>
        </section>
    );
};

// The question set aside: a bar above the composer until it is answered or
// skipped (pen "Väntande fråga").
export const QuestionsWaitingBar = ({
    count,
    onAnswer,
}: {
    readonly count: number;
    readonly onAnswer: () => void;
}): ReactNode => {
    const { i18n } = useLingui();
    return (
        <button
            type="button"
            className="bilda-qsheet-waiting"
            onClick={onAnswer}
        >
            <Icon name="sparkle" className="bilda-qsheet__spark" />
            <span className="bilda-qsheet-waiting__text">
                {i18n._(
                    msg`${plural(count, { one: "# question waiting", other: "# questions waiting" })}`,
                )}
            </span>
            <span className="bilda-qsheet-waiting__answer">
                <Trans>Answer</Trans>
            </span>
        </button>
    );
};
